import React, { useContext, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Panel as BootstrapPanel } from 'react-bootstrap';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ValidationGroup, ErrorMessages } from '@bingads-webui-react/validation';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import {
  Collapse,
  TextTitle,
  ValidatedBootstrapPanel,
  ValidatedPanel,
  getPanelErrorProps,
  useCollapseState,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import { Campaign } from '@bingads-webui/campaign-odata-schemas';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { FormErrorGroup, UnmappedFormErrors, andComp, orComp, entityIs, propertyIs } from '@bingads-webui-campaign-react/form-error';
import { useFormService } from '@bingads-webui-react/form';
import { AdDistributionWithValidationSection } from '@bingads-webui-campaign/ad-distribution';
import { ValidatedLegacyAdSchedule, getAdScheduleDetails } from '@bingads-webui-campaign/ad-schedule';
import { ValidatedLegacyBidStrategy } from '@bingads-webui-campaign/bid-strategy';
import { ValidatedDefaultBid } from '@bingads-webui-campaign-react/default-bid';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { mediumEnum, getBidStrategyTitleDetails, bidStratetyTypeValue } from '@bingads-webui-campaign-react/constants';
import { ERROR_ENTITY } from '@bingads-webui-campaign-react/search-campaign-data';
import { ValidatedDateSetting, getDateSettingDetails } from '@bingads-webui-campaign-react/date-setting';
import { ValidatedLegacySharedBudget, getBudgetTitleDetails } from '@bingads-webui-campaign/shared-budget';
import { PredictiveMatching, getPredictiveMatchingDetails, ValidatedBrandLists, getBrandListsTitleDetails } from '@bingads-webui-campaign-react/search-campaign-wizard-controls';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';

import { getStyles } from './styles';
import { StepHeader } from '../../shared/step-header';
import BudgetImg from '../../shared/media/budget.svg';
import BidStrategyImg from '../../shared/media/bid-strategy.svg';
import PredictiveMatchingImg from '../../shared/media/predictive-matching.svg';
import BrandListsImg from '../../shared/media/brand-lists.svg';

const entityIsAdGroup = entityIs(ERROR_ENTITY.AdGroup);
const entityIsCampaign = entityIs(ERROR_ENTITY.Campaign);
const ServerValidatedLegacyBidStrategy = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedLegacyBidStrategy, { filter: andComp(entityIsCampaign, propertyIs('BiddingScheme')) }));
const ServerValidatedDefaultBid = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedDefaultBid, { filter: andComp(entityIsAdGroup, propertyIs('BidAmount')) }));
const ServerValidatedLegacyAdSchedule = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedLegacyAdSchedule, { filter: entityIs(ERROR_ENTITY.CampaignDayTimeCriterion) }));
const ServerValidatedLegacyDateSetting = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedDateSetting, { filter: andComp(entityIsAdGroup, orComp(propertyIs('StartDate'), propertyIs('EndDate'))) }));
const ServerValidatedLegacySharedBudget = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedLegacySharedBudget, { filter: andComp(entityIsCampaign, propertyIs('Budget')) }));
const ServerValidatedBrandLists = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedBrandLists, { filter: entityIs(ERROR_ENTITY.BrandListAssociation) }));

export const BudgetAndBidsStep = ({
  data,
  onDataChange,
  error,
}) => {
  const {
    serverErrors = [],
    systemErrors = [],
  } = error || {};

  const i18n = useContext(I18nContext);
  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap]), [i18n]);
  const activity = useContext(ActivityContext);
  const classes = useDefaultStyles(getStyles);
  const { state: { context: { saveFailed: showAllValidationErrors } } } = useFormService();
  const [advancedSettingsSectionExpanded, setAdvancedSettingsSectionExpanded] = useState(false);
  const [adScheduleView, setAdScheduleView] = useState(null);
  const onAdvancedSettingsSectionToggle = useCallback((expanded) => {
    setAdvancedSettingsSectionExpanded(expanded);
  }, []);

  const serverContext = useContext(ServerContext);
  const {
    DynamicPermissions,
  } = serverContext;

  const [
    allBudgetAndBidSections,
    budgetAndBidExpandedSections,
    setBudgetAndBidExpandedSections,
  ] = useCollapseState(
    ['budget', 'bid-strategy'] /* all sections */,
    ['budget', 'bid-strategy'] /* initially expanded sections */
  );

  const [
    allAdScheduleSections,
    adScheduleExpandedSections,
    setAdScheduleExpandedSections,
  ] = useCollapseState(
    ['start-end-date', 'ad-schedule'] /* all sections */,
    [] /* initially expanded sections */
  );

  const [
    allPredictiveMatchingSections,
    predictiveMatchingExpandedSections,
    setPredictiveMatchingExpandedSections,
  ] = useCollapseState(
    ['predictive-matching', 'brand-lists'] /* all sections */,
    ['predictive-matching', 'brand-lists'] /* initially expanded sections */
  );

  const [
    allAdDistributionSections,
    adDistributionExpandedSections,
    setAdDistributionExpandedSections,
  ] = useCollapseState(
    ['ad-distribution'] /* all sections */,
    [] /* initially expanded sections */
  );

  const onAdGroupValidate = useCallback((result) => {
    if (data && data.adGroup) {
      onDataChange({
        data: {
          ...data,
          adGroup: {
            ...data.adGroup,
            ...result.data,
          },
        },
        isValid: result.isValid,
      });
    }
  }, [data, onDataChange]);

  const onDistributionValidate = useCallback((result) => {
    if (data && data.adGroup) {
      onDataChange({
        data: {
          ...data,
          adGroup: {
            ...data.adGroup,
            ...result.data,
            Medium: mediumEnum.Search,
          },
        },
        isValid: result.isValid,
      });
    }
  }, [data, onDataChange]);

  const onCampaignValidate = useCallback((result) => {
    if (data && data.campaign) {
      onDataChange({
        data: {
          ...data,
          campaign: {
            ...data.campaign,
            ...result.data,
          },
        },
        isValid: result.isValid,
      });
    }
  }, [data, onDataChange]);

  const budgetAndBidSection = useMemo(() => {
    const biddingScheme = data && data.campaign && data.campaign.BiddingScheme;
    const bidType = biddingScheme && biddingScheme.Type;
    const bidStrategy = biddingScheme && biddingScheme.BidStrategyId;
    let bidOption = bidType;
    if (!bidOption || bidStrategy) {
      bidOption = bidStratetyTypeValue.portfolioBidStrategy;
    }
    const budgetComponent = (
      <ValidatedPanel
        key="budget"
        noOverflowHidden
        title={<TextTitle titleKey={_TL_('Budget')} topicKey="pop_BA_CampaignCreate_Budget_SharedBudget" icon={BudgetImg} details={getBudgetTitleDetails(serverContext, i18n, data && data.campaign && data.campaign.Budget)} />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedLegacySharedBudget
          className="vanila-budget"
          campaignId={data && data.campaign && data.campaign.Id}
          validationProps={{
            data: { Budget: data && data.campaign && data.campaign.Budget },
            onValidate: onCampaignValidate,
            schema: Campaign,
            field: 'Budget',
         }}
        />
      </ValidatedPanel>
    );
    const bidStrategyComponent = (
      <ValidatedPanel
        key="bid-strategy"
        noOverflowHidden
        title={<TextTitle titleKey={_TL_('Bid Strategy')} topicKey="pop_BA_CampaignGrids_BidStrategy" icon={BidStrategyImg} details={getBidStrategyTitleDetails(i18n, bidOption)} />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedLegacyBidStrategy
          entityType="Campaign"
          hideHeader
          id="bid-strategy-search-campaign"
          campaignType="search"
          i18nNewDesign={i18n}
          i18n={i18n}
          isCreation
          isInitialize
          needConfirmManagePortfolio
          autoTargetingEnabled={data && data.campaign && data.campaign.AutoTargetingEnabled}
          budgetAmount={data && data.campaign && data.campaign.Budget && data.campaign.Budget.Amount}
          bidStrategyName={data && data.campaign && data.campaign.BidStrategyName}
          validationProps={{
            data: { BiddingScheme: data && data.campaign && data.campaign.BiddingScheme },
            schema: Campaign,
            field: 'BiddingScheme',
            onValidate: onCampaignValidate,
          }}
          activityLogger={activity}
        />
        {bidType === 'EnhancedCpc' &&
          <ServerValidatedDefaultBid
            Type="EnhancedCpc"
            i18n={i18n}
            field="SearchBid"
            id="default-bid"
            label={i18n.getString(_TL_('Default bid'))}
            value={data && data.adGroup && data.adGroup.SearchBid}
            onValidate={onAdGroupValidate}
          />
        }
      </ValidatedPanel>
    );
    const budgetAndBidSections = [
      budgetComponent,
      bidStrategyComponent,
    ];
    return (
      <div className={classNames(classes.budgetAndBidSection, 'budget-and-bidstrategy-section')}>
        <Collapse allPanelItems={allBudgetAndBidSections} checkItems skipItemsTypeCheck activeKeys={budgetAndBidExpandedSections} setActiveKeys={setBudgetAndBidExpandedSections}>
          { budgetAndBidSections }
        </Collapse>
      </div>
    );
  }, [activity, allBudgetAndBidSections, budgetAndBidExpandedSections, classes.budgetAndBidSection, data, i18n, onAdGroupValidate, onCampaignValidate, serverContext, setBudgetAndBidExpandedSections]);

  const performanceSection = useMemo(() => {
    /* const campaignPerformanceComponent = (
      <ValidatedFixedPanel
        key="campaign-performance"
        title={<TextTitle titleKey={_TL_('Campaign performance')} topicKey="" />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        campaign perfromance
      </ValidatedFixedPanel>
    );
    const monthlyPerformanceComponent = (
      <ValidatedFixedPanel
        key="monthly-performance"
        title={<TextTitle titleKey={_TL_('Estimated monthly performance')} topicKey="" />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        monthly performance
      </ValidatedFixedPanel>
    ); */
    const performanceSections = [
    ];
    return (
      <div className={classNames('performance-section')}>
        <Collapse allPanelItems={['campaign-performance', 'monthly-performance']} checkItems skipItemsTypeCheck activeKeys={['campaign-performance', 'monthly-performance']}>
          { performanceSections }
        </Collapse>
      </div>
    );
  }, []);

  const advancedSettingsSection = useMemo(() => {
    const dateSettingComponent = (
      <ValidatedPanel
        key="start-end-date"
        noOverflowHidden
        title={<TextTitle titleKey={_TL_('Start and end dates')} details={`${getDateSettingDetails(i18n, data && data.adGroup && data.adGroup.StartDate, data && data.adGroup && data.adGroup.EndDate)}`} />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedLegacyDateSetting
          i18n={i18n}
          validationProps={{
            onValidate: onAdGroupValidate,
            stopPropagation: true,
            data: {
              StartDate: data && data.adGroup && data.adGroup.StartDate,
              EndDate: data && data.adGroup && data.adGroup.EndDate,
              TimeZone: data && data.campaign && data.campaign.TimeZone,
              isCreate: true,
              status: data && data.adGroup && data.adGroup.Status,
            },
          }}
        />
      </ValidatedPanel>
    );
    const adScheduleComponent = (
      <ValidatedPanel
        key="ad-schedule"
        noOverflowHidden
        title={<TextTitle titleKey={_TL_('Ad schedule')} topicKey="pop_BA_Targeting_DayTime" details={`${getAdScheduleDetails(i18n, adScheduleView && adScheduleView.current)}`} />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedLegacyAdSchedule
          entity={{
            data: {
              TimeZone: data && data.campaign && data.campaign.TimeZone,
              AdScheduleUseSearcherTimeZone: data && data.campaign && data.campaign.AdScheduleUseSearcherTimeZone,
              Id: data && data.campaign && data.campaign.Id,
            },
          }}
          validationProps={{
            field: 'AdSchedule',
            onValidate: onCampaignValidate,
            data: {
              dayTimeRanges: data && data.campaign && data.campaign.dayTimeRanges,
            },
          }}
          setAdScheduleView={setAdScheduleView}
          showBidAdjustmentColumn
        />
      </ValidatedPanel>
    );

    const adScheduleSection = [
      adScheduleComponent,
      dateSettingComponent,
    ];

    const supportedBiddingStrategiesForPredictiveMatching = [
      bidStratetyTypeValue.targetCpa,
      bidStratetyTypeValue.targetRoas,
      bidStratetyTypeValue.maximizeConversions,
    ];

    const biddingScheme = data && data.campaign && data.campaign.BiddingScheme.Type;

    const showPredictiveMatching = DynamicPermissions && DynamicPermissions.PredictiveMatching && (supportedBiddingStrategiesForPredictiveMatching.includes(biddingScheme) || DynamicPermissions.PredictiveMatchingPhase2);
    const showBrandLists = DynamicPermissions && DynamicPermissions.PredictiveMatching && DynamicPermissions.PredictiveMatchingPhase2;

    return (
      <div className={classNames('advanced-bid-and-schedule-section')}>
        <ValidatedBootstrapPanel
          onToggle={onAdvancedSettingsSectionToggle}
          expanded={advancedSettingsSectionExpanded}
          className={classNames(classes.collapsiblePanel, 'advanced-settings-panel')}
          render={() => (
            <>
              <BootstrapPanel.Heading>
                <BootstrapPanel.Title toggle>
                  {i18n.getString(_TL_('Advanced settings'))}
                </BootstrapPanel.Title>
              </BootstrapPanel.Heading>
              <BootstrapPanel.Collapse>
                <BootstrapPanel.Body>
                  <div className={classNames('advanced-settings-section')}>
                    <Collapse allPanelItems={allPredictiveMatchingSections} checkItems skipItemsTypeCheck activeKeys={predictiveMatchingExpandedSections} setActiveKeys={setPredictiveMatchingExpandedSections}>
                      { showPredictiveMatching &&
                        <ValidatedPanel
                          key="predictive-matching"
                          noOverflowHidden
                          title={
                            <TextTitle
                              titleKey={_TL_('Predictive matching')}
                              icon={PredictiveMatchingImg}
                              topicKey="pop_BA_Keywords_PredictiveMatching"
                              details={getPredictiveMatchingDetails(i18n, data && data.campaign && (data.campaign.isPredictiveMatchingChecked === undefined ? data.campaign.AutoTargetingEnabled : data.campaign.isPredictiveMatchingChecked))}
                              isRecommended
                            />
                          }
                          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                        >
                          <PredictiveMatching
                            i18n={i18n}
                            onValidate={onCampaignValidate}
                            isPredictiveMatchingChecked={data && data.campaign && data.campaign.isPredictiveMatchingChecked}
                            AutoTargetingEnabled={data && data.campaign && data.campaign.AutoTargetingEnabled}
                            disabled={!supportedBiddingStrategiesForPredictiveMatching.includes(biddingScheme)}
                          />
                        </ValidatedPanel>
                      }
                      { showBrandLists && (
                        <ValidatedPanel
                          key="brand-lists"
                          noOverflowHidden
                          title={
                            <TextTitle
                              titleKey={_TL_('Brand safety')}
                              icon={BrandListsImg}
                              details={getBrandListsTitleDetails(i18n, data && data.campaign && data.campaign.brandProtections)}
                              isRecommended
                            />
                          }
                          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                        >
                          <ServerValidatedBrandLists
                            campaignId={data && data.campaign && data.campaign.Id}
                            validationProps={{
                              initialValue: data && data.campaign,
                              data: data && data.campaign,
                              onValidate: onCampaignValidate,
                            }}
                            disabled={!(data && data.campaign && (data.campaign.isPredictiveMatchingChecked === undefined ? data.campaign.AutoTargetingEnabled : data.campaign.isPredictiveMatchingChecked))}
                          />
                        </ValidatedPanel>
                      )}
                    </Collapse>
                    <div className={classNames('ad-schedule-section')}>
                      <h3 className="section-title">
                        {i18n.getString(_TL_('Ad schedule'))}
                        <HelpPopup
                          topic="pop_BA_Targeting_DayTime"
                          aria-label={i18n.getString(_TL_('Click to learn more about Ad schedule'))}
                        />
                      </h3>
                      <div className="section-description">
                        <span >{i18n.getString(_TL_('Choose when you want your ads to run.'))}</span>
                      </div>
                    </div>
                    <Collapse allPanelItems={allAdScheduleSections} checkItems skipItemsTypeCheck activeKeys={adScheduleExpandedSections} setActiveKeys={setAdScheduleExpandedSections}>
                      { adScheduleSection }
                    </Collapse>
                    <div className={classNames('ad-distribution-section')}>
                      <h3 className="section-title">
                        {i18n.getString(_TL_('Ad distribution'))}
                        <HelpPopup
                          topic="pop_BA_NewCampaignCreate_AdDistribution"
                          aria-label={i18n.getString(_TL_('Click to learn more about Ad distribution'))}
                        />
                      </h3>
                      <div className="section-description">
                        <span >{i18n.getString(_TL_('Choose where you want to show your ad on the Microsoft Advertising Network.'))}</span>
                      </div>
                    </div>
                    <Collapse allPanelItems={allAdDistributionSections} checkItems skipItemsTypeCheck activeKeys={adDistributionExpandedSections} setActiveKeys={setAdDistributionExpandedSections}>
                      <AdDistributionWithValidationSection
                        data={data && data.adGroup}
                        onValidate={onDistributionValidate}
                        i18n={i18n}
                        key="ad-distribution"
                        noOverflowHidden
                      />
                    </Collapse>
                  </div>
                </BootstrapPanel.Body>
              </BootstrapPanel.Collapse>
            </>
            )}
        />
      </div>
    );
  }, [DynamicPermissions, adDistributionExpandedSections, adScheduleExpandedSections, adScheduleView, advancedSettingsSectionExpanded, allAdDistributionSections, allAdScheduleSections, allPredictiveMatchingSections, classes.collapsiblePanel, data, i18n, onAdGroupValidate, onAdvancedSettingsSectionToggle, onCampaignValidate, onDistributionValidate, predictiveMatchingExpandedSections, setAdDistributionExpandedSections, setAdScheduleExpandedSections, setPredictiveMatchingExpandedSections]);

  return (
    <div className={classNames(classes.budgetAndBidsStep, 'budget-and-bids-step')}>
      <ActuationGroup actuationProps={{ isActive: showAllValidationErrors }}>
        <AttentionBeaconGroup>
          <FormErrorGroup formErrorProps={{ errors: serverErrors, showAllValidationErrors }}>
            <ValidationGroup
              validationProps={{
                i18n: legacyI18n,
              }}
            >
              <StepHeader
                title={_TL_('How much do you want to spend?')}
                description={_TL_('Set your campaign budget and choose your bid strategy.')}
              />
              <div className="server-errors">
                <UnmappedFormErrors />
              </div>
              <div className="system-errors">
                <ErrorMessages messages={systemErrors} />
              </div>
              <div className={classes.formContainer}>
                <div className={classNames('left-part')}>
                  { budgetAndBidSection }
                  { advancedSettingsSection }
                </div>
                <div className={classNames('right-part')} >
                  {performanceSection}
                </div>
              </div>
            </ValidationGroup>
          </FormErrorGroup>
        </AttentionBeaconGroup>
      </ActuationGroup>
    </div>
  );
};

BudgetAndBidsStep.propTypes = {
  data: PropTypes.shape({}).isRequired,
  onDataChange: PropTypes.func.isRequired,
  error: PropTypes.shape({
    serverErrors: PropTypes.arrayOf(PropTypes.shape({})),
    systemErrors: PropTypes.arrayOf(PropTypes.shape({})),
  }),
};

BudgetAndBidsStep.defaultProps = {
  error: null,
};
